import * as React from 'react';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';

import { getFullLocationString, getPaymentTypeString } from 'utils';
import { EPaymentMethod } from 'enums';
import { theme } from 'configs';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { BillingAddressDialog, PaymentMethodDialog, ShippingAddressDialog } from 'dialogs/payment';
import { updateLayout } from 'store/slices/layout.slice';

interface ProfileBillingPageProps {}

export const ProfileBillingPage: React.FC<ProfileBillingPageProps> = () => {
  const dispatch = useAppDispatch();
  const {
    billingAddress,
    shippingAddress,
    paymentMethod: pmType,
    cardLastFourCode: pmLastFour,
    nameOnPayment: name,
    billingEmail: email,
  } = useAppSelector(selectCurrentUserInfo);
  const [visibleBillingAddressDialog, setVisibleBillingAddressDialog] = React.useState<boolean>(false);
  const [visibleShippingAddressDialog, setVisibleShippingAddressDialog] = React.useState<boolean>(false);
  const [visiblePaymentMethodDialog, setVisiblePaymentMethodDialog] = React.useState<boolean>(false);

  React.useEffect(() => {
    dispatch(
      updateLayout({
        headerTitle: 'Billing Information',
      }),
    );
  }, []);

  const billingInfoCards = [
    {
      title: 'Billing Address',
      subtitle: getFullLocationString(billingAddress),
      onEdit: () => setVisibleBillingAddressDialog(true),
    },
    {
      title: 'Shipping Address',
      subtitle: getFullLocationString(shippingAddress),
      onEdit: () => setVisibleShippingAddressDialog(true),
    },
    {
      title: 'Payment Method',
      subtitle: (
        <PaymentMethodInfo pmType={pmType as EPaymentMethod} name={name} email={email} pmLastFour={pmLastFour} />
      ),
      onEdit: () => setVisiblePaymentMethodDialog(true),
    },
  ];

  return (
    <>
      <Typography variant="h5">Billing Information</Typography>

      <Typography
        sx={{
          fontWeight: 'normal',
          marginTop: 1,
          marginBottom: 4,
          color: theme.palette.text.secondary,
        }}
      >
        Update your billing account information
      </Typography>

      <Grid
        container
        spacing={6}
        sx={{
          marginTop: 1,
        }}
      >
        {billingInfoCards.map((cardInfo, index) => (
          <BillingInfoCard key={`billing-info-card-${index}`} {...cardInfo} />
        ))}
      </Grid>

      <BillingAddressDialog visible={visibleBillingAddressDialog} setVisible={setVisibleBillingAddressDialog} />

      <ShippingAddressDialog visible={visibleShippingAddressDialog} setVisible={setVisibleShippingAddressDialog} />

      <PaymentMethodDialog visible={visiblePaymentMethodDialog} setVisible={setVisiblePaymentMethodDialog} />
    </>
  );
};

interface BillingInfoCardProps {
  title?: string;
  subtitle?: string | React.ReactNode;
  onEdit?: () => void;
}

const BillingInfoCard = ({ title = '', subtitle = '', onEdit = () => {} }: BillingInfoCardProps) => {
  return (
    <Grid item xs={12}>
      <Paper>
        <Box sx={{ display: 'flex', alignItems: 'center', paddingX: { xs: 2, sm: 4 }, paddingY: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 18 }}>{title}</Typography>

            {typeof subtitle === 'string' ? (
              <Typography variant="subtitle2" color="textSecondary" sx={{ marginTop: 0.5 }}>
                {subtitle}
              </Typography>
            ) : (
              subtitle
            )}
          </Box>

          <Box sx={{ marginLeft: 'auto' }}>
            <IconButton color="primary" onClick={onEdit}>
              <Edit fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

interface PaymentMethodInfoProps {
  pmType: EPaymentMethod;
  name: string;
  email: string;
  pmLastFour: string;
}

const PaymentMethodInfo = ({ pmType, name, email, pmLastFour }: PaymentMethodInfoProps) => {
  return pmLastFour ? (
    <>
      <Typography variant="subtitle2" color="textSecondary" sx={{ marginTop: 0.5 }}>
        Type: {getPaymentTypeString(pmType)}
      </Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ marginTop: 0.5 }}>
        Name: {name}
      </Typography>

      {pmType === EPaymentMethod.INVOICE && (
        <Typography variant="subtitle2" color="textSecondary" sx={{ marginTop: 0.5 }}>
          Email: {email}
        </Typography>
      )}

      {pmType === EPaymentMethod.CARD && (
        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 0.5 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Card:
          </Typography>

          <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: 9, marginLeft: 1 }}>
            xxxx xxxx xxxx xxxx
          </Typography>

          <Typography variant="subtitle2" color="textSecondary" sx={{ marginLeft: 1 }}>
            {pmLastFour}
          </Typography>
        </Box>
      )}
    </>
  ) : (
    <Typography variant="subtitle2" color="textSecondary" sx={{ marginTop: 0.5 }}>
      -
    </Typography>
  );
};
